import React, { useState } from 'react';
import { ArrowLeft, X, Save, RefreshCw } from 'lucide-react';

/**
 * Preview modal for report documents
 * Extracted from ModernForm.jsx to improve modularity
 */
const FormPreviewModal = ({ 
  draft, 
  onClose, 
  generateBodyText, 
  generateLimitationsText,
  setFormData 
}) => {
  const [activeTab, setActiveTab] = useState('preview');
  const [documentText, setDocumentText] = useState(() => {
    if (draft.fullDocumentText) {
      return draft.fullDocumentText;
    }
    return typeof generateBodyText === 'function' ? generateBodyText() : '';
  });
  const [hasChanges, setHasChanges] = useState(false);

  const formatPreviewText = (text) => {
    if (!text) return <div>No content to preview</div>;
    
    const sections = text.split(/(?=Utilizing electronic|No other metal|If you have|Terms and Conditions)/g);
    
    const formatSection = (section) => {
      if (!section) return null;

      if (section.includes("Smart Choice Inspections LLC")) {
        const baseTextEnd = section.match(/(.*?(?:size of a UST\.|was detected[^.]*\.|was identified[^.]*\.))/);
        if (!baseTextEnd) return <div className="whitespace-pre-wrap">{section}</div>;

        const beforeText = baseTextEnd[1];
        const remainingText = section.substring(beforeText.length);

        const conclusionMatch = remainingText.match(/(This does not guarantee|We recommend requesting|This UST may pose)/);
        const conclusionIndex = conclusionMatch ? remainingText.indexOf(conclusionMatch[0]) : -1;

        const middleText = conclusionIndex !== -1 
          ? remainingText.substring(0, conclusionIndex) 
          : remainingText;

        const limitationsIndex = middleText.indexOf("However,");
        
        let customText = '';
        let limitationsText = '';
        let conclusionText = conclusionIndex !== -1 ? remainingText.substring(conclusionIndex) : '';

        if (limitationsIndex !== -1) {
          customText = middleText.substring(0, limitationsIndex).trim();
          limitationsText = middleText.substring(limitationsIndex).trim();
        } else {
          customText = middleText.trim();
        }

        return (
          <div className="whitespace-pre-wrap">
            {beforeText}
            {customText && customText !== '.' && (
              <span style={{ color: '#2563eb' }} className="font-medium">
                {' ' + customText.trim() + (customText.trim().endsWith('.') ? '' : '.')}
              </span>
            )}
            {limitationsText && (
              <span style={{ color: '#dc2626' }} className="font-medium">
                {' ' + limitationsText}
              </span>
            )}
            {conclusionText && ' ' + conclusionText}
          </div>
        );
      }
      
      return <div className="whitespace-pre-wrap">{section}</div>;
    };
    
    return (
      <div className="space-y-8">
        {sections.map((section, index) => (
          <div key={index} className="font-mono">
            {formatSection(section)}
          </div>
        ))}
      </div>
    );
  };

  const handleReset = () => {
    if (confirm('Reset text to default? This will remove any custom edits.')) {
      setDocumentText(generateBodyText());
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    if (confirm('Save changes to this document?')) {
      setFormData(prev => ({
        ...prev,
        fullDocumentText: documentText,
        customText: ''
      }));
      
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-0 z-50">
      <div className="bg-white w-full h-full md:h-auto md:rounded-lg md:max-w-4xl md:max-h-[90vh] flex flex-col">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <button
              onClick={onClose}
              className="text-slate-600 hover:text-slate-800 flex items-center gap-1 sm:hidden"
            >
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              )}
              <button onClick={onClose} className="hidden sm:block">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex-1 max-w-[160px] px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'preview' 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Preview
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              className={`flex-1 max-w-[160px] px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'edit' 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Edit Document
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-auto p-6 pb-24 sm:pb-6">
          {activeTab === 'preview' ? (
            <div className="text-slate-700">
              {formatPreviewText(documentText)}
            </div>
          ) : (
            <div className="space-y-4 h-full">
              <div className="text-sm text-slate-500 bg-yellow-50 p-4 rounded-lg">
                ⚠️ You can edit any part of the document. Changes will be saved for this report only.
              </div>
              
              <textarea
                className="w-full h-[calc(100vh-280px)] md:h-[600px] p-4 border rounded font-mono text-sm"
                value={documentText}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setDocumentText(newValue);
                  setHasChanges(true);
                }}
              />
            </div>
          )}
        </div>

        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t">
          <div className="grid grid-cols-2 gap-2 p-4">
            <button
              onClick={handleReset}
              className="w-full px-4 py-3 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center justify-center gap-1"
            >
              <RefreshCw className="w-4 h-4" /> Reset Text
            </button>
            <button
              onClick={handleSave}
              className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg flex items-center justify-center gap-1 disabled:opacity-50"
              disabled={!hasChanges}
            >
              <Save className="w-4 h-4" /> Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPreviewModal;
